import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiX, FiSearch } from 'react-icons/fi';

const normalizeOption = (option) => {
  if (typeof option === 'string') {
    return {
      value: option,
      label: option,
      symbol: option,
      price: null,
      change24h: null,
      icon: null
    };
  }

  if (option == null) {
    return null;
  }

  return {
    value: option.value ?? option.symbol ?? option.label,
    label: option.label ?? option.name ?? option.symbol ?? option.value,
    symbol: option.symbol ?? option.value ?? option.label,
    price: option.price ?? option.current_price ?? null,
    change24h: option.change24h ?? option.price_change_percentage_24h ?? null,
    icon: option.icon ?? option.image ?? null,
    extra: option
  };
};

const CoinSelectorControl = ({
  label,
  value,
  onChange,
  options = [],
  helperText,
  disabled = false,
  className = '',
  buttonClassName = '',
  placeholder = 'Select coin',
  showPrice = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const normalizedOptions = useMemo(() => {
    return options
      .map(normalizeOption)
      .filter(Boolean)
      .map((option, index) => ({ ...option, key: `${option.value}-${index}` }));
  }, [options]);

  const selectedOption = useMemo(() => {
    if (!value) return normalizedOptions[0];
    return normalizedOptions.find((option) => option.value === value || option.symbol === value) || normalizedOptions[0];
  }, [normalizedOptions, value]);

  const filteredOptions = useMemo(() => {
    if (!search) return normalizedOptions;
    const lower = search.toLowerCase();
    return normalizedOptions.filter((option) =>
      option.label?.toLowerCase().includes(lower) || option.symbol?.toLowerCase().includes(lower)
    );
  }, [normalizedOptions, search]);

  const handleSelect = (option) => {
    if (disabled) return;
    if (onChange) {
      onChange(option.value, option.extra ?? option);
    }
    setIsOpen(false);
    setSearch('');
  };

  const renderIcon = (option, sizeClasses = 'h-10 w-10') => {
    if (option?.icon) {
      return <img src={option.icon} alt={option.label} className={`${sizeClasses} rounded-full object-cover`} />;
    }
    return (
      <div className={`${sizeClasses} rounded-full bg-gradient-to-br from-blue-500/20 via-blue-400/10 to-purple-500/20 flex items-center justify-center border border-white/10`}>
        <span className="text-xs font-semibold text-white/90">{option?.symbol?.slice(0, 3)?.toUpperCase() || '?'}</span>
      </div>
    );
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label ? (
        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-300/70">{label}</label>
      ) : null}

      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
        className={`flex w-full items-center justify-between rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-left transition hover:border-blue-500 hover:bg-black/60 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
      >
        <div className="flex items-center gap-3">
          {selectedOption ? renderIcon(selectedOption) : renderIcon({ symbol: '?' })}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-white/50">{placeholder}</p>
            <p className="text-base font-semibold text-white">
              {selectedOption ? selectedOption.label : '—'}
            </p>
          </div>
        </div>
        <FiChevronDown className="text-white/70" />
      </button>

      {helperText ? <p className="text-xs text-gray-400">{helperText}</p> : null}

      {isOpen ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 overflow-y-auto" onClick={() => { setIsOpen(false); setSearch(''); }}>
          <div className="flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-gray-900 via-gray-950 to-black shadow-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-blue-200">Select Asset</p>
                <p className="text-lg font-semibold text-white">Choose a coin to continue</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setSearch('');
                }}
                className="rounded-full bg-white/10 p-2 text-gray-200 transition hover:bg-white/20"
              >
                <FiX />
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="relative mb-4">
                <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search coin..."
                  autoFocus
                  className="w-full rounded-xl border border-white/10 bg-black/40 py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-gray-500 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="max-h-[60vh] overflow-y-auto rounded-xl border border-white/5">
                <div className="grid grid-cols-1 gap-2">
                  {filteredOptions.length === 0 ? (
                    <div className="px-4 py-6 text-center text-gray-400">
                      No assets match your search.
                    </div>
                  ) : (
                    filteredOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left transition hover:border-blue-400 hover:bg-white/10 ${
                          option.value === selectedOption?.value ? 'border-blue-400 bg-white/10' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {renderIcon(option, 'h-10 w-10')}
                          <div>
                            <p className="text-sm font-semibold text-white">{option.label}</p>
                            <p className="text-xs uppercase tracking-wide text-white/60">{option.symbol}</p>
                          </div>
                        </div>
                        <span className="rounded-lg bg-blue-500/80 px-3 py-1 text-xs font-semibold text-white">
                          {option.value === selectedOption?.value ? 'Selected' : 'Choose'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CoinSelectorControl;
